import { Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { BillingFrequency, FrequencyTotals } from '@proposify/pricing-engine';
import { TotalRowTypesIdentifier } from '../../../grid/reduxStore/table.types';
import { getFooterCombinedTotals } from '../Table/PricingTotalsFooter/getFooterCombinedTotals';
import { getFrequencyFooterData } from '../Table/PricingTotalsFooter/frequencyFooterRows';
import { FooterWrapper, FORMATTER_KEY, TITLE_KEY } from '../Table/PricingTotalsFooter/PricingTableTotalsFooter';
import { ToggledInputWrapper, TitleTextWrapper } from '../Table/PricingTotalsFooter/ToggledInput';
import { updateTotalRowsWithSubtotalCalculation } from '../Table/tableUtils/updateTotalRowsWithSubtotalCalculation';
import TextEditorForTableBlock from '../../TiptapEditor/components/TextEditorForTableBlock.tsx';
import PreviewPricingTableTotalFooterRow from './PreviewPricingTableTotalFooterRow.tsx';
import type { CurrencySettings } from '../../../../../services/documents/DocumentRepository.ts';
import { isJson } from '../../../../../utils/isJson.ts';

interface PreviewTableFooterProps {
  updatedTotalRows: any[];
  sumOfSubtotalColumn: number | string;
  pricingDecimalPlaces: number;
  isTemplate?: boolean;
  currencySettings?: CurrencySettings;
  presentFrequencies?: BillingFrequency[];
  frequencyTotals?: FrequencyTotals | null;
}

function PreviewTableFooter({
  isTemplate,
  updatedTotalRows,
  sumOfSubtotalColumn,
  pricingDecimalPlaces,
  currencySettings,
  presentFrequencies,
  frequencyTotals,
}: PreviewTableFooterProps) {
  const { t } = useTranslation();
  const frequencyFooter = useMemo(
    () => getFrequencyFooterData(presentFrequencies ?? [], frequencyTotals ?? null),
    [presentFrequencies, frequencyTotals]
  );
  const showFrequencySubtotals = frequencyFooter.hasNonOneTime;
  const totalRows = useMemo(
    () => updateTotalRowsWithSubtotalCalculation(updatedTotalRows ?? [], Number(sumOfSubtotalColumn)),
    [updatedTotalRows, sumOfSubtotalColumn]
  );
  const combinedTotals = useMemo(
    () => (showFrequencySubtotals ? getFooterCombinedTotals(String(sumOfSubtotalColumn), updatedTotalRows ?? []) : null),
    [showFrequencySubtotals, sumOfSubtotalColumn, updatedTotalRows]
  );

  return (
    <FooterWrapper className="py-preview-table-style">
      {totalRows?.map((row) => {
        const { isRowHidden } = row;
        const isRelabeledSubtotal = showFrequencySubtotals && row.rowType === TotalRowTypesIdentifier.SUBTOTAL;
        const combinedRowValue = combinedTotals?.rowValues[String(row.id)];
        const rowValue =
          row.rowType === TotalRowTypesIdentifier.SUBTOTAL
            ? isRelabeledSubtotal
              ? frequencyFooter.oneTimeTotal
              : sumOfSubtotalColumn
            : combinedRowValue ?? row.subtotal;
        const value = row[FORMATTER_KEY];
        const titleValue = row[TITLE_KEY];
        const isValueInTipTapFormat =
          typeof titleValue === 'string' && (titleValue.includes('<h') || titleValue.includes('<p') || isJson(titleValue));
        const titleEditorValue = isJson(titleValue) ? JSON.parse(titleValue) : titleValue;
        if (isRowHidden) return null;

        const footerRow = (
          <PreviewPricingTableTotalFooterRow
            key={row.id}
            rowType={row.rowType as TotalRowTypesIdentifier}
            subtotalValue={rowValue}
            priceValue={value}
            isRowSelected={false}
            currencySign={currencySettings?.currencySign}
            pricingDecimalPlaces={pricingDecimalPlaces}
            calcInputComponent={null}
            titleComponent={
              isRelabeledSubtotal ? (
                <span className="py-numeric-cell-value" data-testid="one-time-subtotal-title">
                  {t('pricing.footer.frequency_subtotal.one_time')}
                </span>
              ) : (
                <ToggledInputWrapper>
                  {isValueInTipTapFormat ? (
                    <TextEditorForTableBlock isTemplate={isTemplate} isPreview={true} editable={false} value={titleEditorValue} />
                  ) : (
                    <TitleTextWrapper>{row.title}</TitleTextWrapper>
                  )}
                </ToggledInputWrapper>
              )
            }
          />
        );

        if (!isRelabeledSubtotal) return footerRow;

        return (
          <Fragment key={row.id}>
            {footerRow}
            {frequencyFooter.frequencyRows.map((frequencyRow) => (
              <PreviewPricingTableTotalFooterRow
                key={`frequency-subtotal-${frequencyRow.frequency}`}
                rowType={TotalRowTypesIdentifier.SUBTOTAL}
                testIdOverride={`frequency-subtotal-${frequencyRow.frequency}`}
                subtotalValue={frequencyRow.total}
                priceValue=""
                isRowSelected={false}
                currencySign={currencySettings?.currencySign}
                pricingDecimalPlaces={pricingDecimalPlaces}
                calcInputComponent={null}
                titleComponent={
                  <span className="py-numeric-cell-value" data-testid={`frequency-subtotal-title-${frequencyRow.frequency}`}>
                    {t(frequencyRow.labelKey, { defaultValue: frequencyRow.fallbackLabel })}
                  </span>
                }
              />
            ))}
          </Fragment>
        );
      })}
    </FooterWrapper>
  );
}

export default PreviewTableFooter;
